import { useCallback, useEffect, useMemo, useState } from "react";
import { formatApiError } from "@/lib/api";
import { isEmbeddedBrowserContext, supportsDirectoryPicker } from "@/lib/cfdiExportFolder";
import {
  buildExportCacheKey,
  clearLastExportMeta,
  formatCacheAge,
  getCachedExportZip,
  getLastExportMeta,
  saveCachedExportZip,
} from "@/lib/exportPeriodZipCache";
import {
  fetchExportManifest,
  fetchExportPeriodCount,
  fetchExportPeriodZipCache,
  fetchExportZipBlob,
  fetchCfdiXmlBlob,
  triggerBlobDownload,
} from "@/lib/exportDirectorioApi";
import { writeManifestToDirectory } from "@/lib/exportDirectorioFs";
import {
  getStoredTaxRepoRootLabel,
  mapManifestItemToTaxRepositoryPath,
  taxRepositoryPathHint,
  TAX_REPO_ROOT_FOLDER,
  isStoredTaxRepoRootReady,
} from "@/lib/exportTaxRepositoryPaths";
import {
  getWritableTaxRepoRootHandle,
  isPickerUserAbort,
  pickAndSaveTaxRepoRootDirectory,
  validateStoredTaxRepoRootAccess,
} from "@/lib/exportTaxRepositoryRoot";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  assessExportBeforeGenerate,
  assessZipBlobAfterDownload,
  daysInPeriod,
  EXPORT_LIMITS,
} from "@/lib/exportPeriodLimits";
import { Checkbox } from "@/components/ui/checkbox";
import { Download, FolderOpen, Loader2, ChevronDown, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function direccionLabel(dir) {
  if (dir === "todos") return "emitidos y recibidos";
  if (dir === "recibido") return "recibidos";
  return "emitidos";
}

function formatPeriodoLegible(fechaInicio, fechaFin) {
  if (!fechaInicio || !fechaFin) return "—";
  return `${fechaInicio} al ${fechaFin}`;
}

/**
 * Exportación MongoDB → disco/ZIP (read-only). Sin contacto con SAT/FIEL.
 */
export default function ExportToolbar({
  clientId,
  clientRfc,
  fechaInicio,
  fechaFin,
  direccion = "emitido",
  /** Si el visor permite exportación combinada (muestra el toggle). */
  allowAmbosToggle = true,
  /** Valor inicial del toggle «emitidos y recibidos». */
  defaultIncluirAmbos = false,
  /** Forzar modo combinado sin toggle (hub SAT). */
  showAmbosFlujos = false,
  /** Hub: solo exportación combinada; sin filas «este visor». */
  hubMode = false,
  /** Alinear RFC/tipo con GET /cfdi/list (visor SAT). */
  exportAlignVisor = false,
  /** Tipo en «este visor» (p. ej. I en ingresos/egresos). */
  exportTipo = null,
  disabled = false,
  compact = true,
  className = "",
}) {
  const [busy, setBusy] = useState(false);
  const [rootBusy, setRootBusy] = useState(false);
  const [periodCountPanel, setPeriodCountPanel] = useState(null);
  const [periodCountTodos, setPeriodCountTodos] = useState(null);
  const [sinXmlPanel, setSinXmlPanel] = useState(0);
  const [sinXmlTodos, setSinXmlTodos] = useState(0);
  const [taxRepoRootReady, setTaxRepoRootReady] = useState(() => isStoredTaxRepoRootReady());
  const [lastCache, setLastCache] = useState(null);
  const [serverCache, setServerCache] = useState(null);
  const [incluirAmbos, setIncluirAmbos] = useState(
    () => Boolean(showAmbosFlujos || hubMode || defaultIncluirAmbos),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingExport, setPendingExport] = useState(null);
  const embeddedPreview = isEmbeddedBrowserContext();

  const periodReady = Boolean(fechaInicio && fechaFin && fechaFin >= fechaInicio);
  const panelDir = (direccion || "emitido").trim().toLowerCase();
  /** ZIP/carpeta del periodo: emitidos y recibidos en el mismo archivo. */
  const exportAmbosFlujos = Boolean(showAmbosFlujos || hubMode || incluirAmbos);

  const paramsForDir = useCallback(
    (dir, includePdf) => {
      const scopeDir = dir === "todos" ? "todos" : dir;
      const q = {
        client_id: clientId,
        fecha_inicio: fechaInicio,
        fecha_fin: fechaFin,
        direccion: scopeDir,
        include_pdf: includePdf,
      };
      if (exportAlignVisor || scopeDir === "todos") q.align_visor = true;
      if (exportTipo && scopeDir !== "todos" && scopeDir === panelDir) {
        q.tipo = exportTipo;
      }
      return q;
    },
    [clientId, fechaInicio, fechaFin, exportAlignVisor, exportTipo, panelDir],
  );

  useEffect(() => {
    setIncluirAmbos(Boolean(showAmbosFlujos || hubMode || defaultIncluirAmbos));
  }, [clientId, fechaInicio, fechaFin, showAmbosFlujos, hubMode, defaultIncluirAmbos]);

  useEffect(() => {
    clearLastExportMeta();
    setLastCache(null);
    setServerCache(null);
  }, [clientId, fechaInicio, fechaFin, incluirAmbos]);

  useEffect(() => {
    if (!clientId || !periodReady || disabled) {
      setServerCache(null);
      return;
    }
    const ac = new AbortController();
    const dirScope = exportAmbosFlujos ? "todos" : panelDir;
    fetchExportPeriodZipCache(paramsForDir(dirScope, false), ac.signal)
      .then((data) => setServerCache(data?.cached ? data : null))
      .catch(() => setServerCache(null));
    return () => ac.abort();
  }, [clientId, periodReady, disabled, exportAmbosFlujos, panelDir, paramsForDir]);

  const refreshLastCache = useCallback(async () => {
    const meta = getLastExportMeta();
    if (!meta?.cacheKey || meta.clientId !== clientId) {
      setLastCache(null);
      return;
    }
    if (!meta.cacheKey.includes(`|${fechaInicio}|${fechaFin}|`)) {
      setLastCache(null);
      return;
    }
    const hit = await getCachedExportZip(meta.cacheKey);
    if (hit) {
      setLastCache({ ...meta, ...hit });
    } else {
      setLastCache(null);
    }
  }, [clientId, fechaInicio, fechaFin]);

  const refreshRootState = useCallback(() => {
    setTaxRepoRootReady(isStoredTaxRepoRootReady());
    getStoredTaxRepoRootLabel();
  }, []);

  useEffect(() => {
    refreshRootState();
  }, [refreshRootState]);

  useEffect(() => {
    refreshLastCache();
  }, [refreshLastCache, periodReady]);

  useEffect(() => {
    if (!clientId || !periodReady || disabled) {
      setPeriodCountPanel(null);
      setPeriodCountTodos(null);
      return;
    }
    const ac = new AbortController();
    const timer = setTimeout(() => {
      if (!hubMode) {
        fetchExportPeriodCount(paramsForDir(panelDir, false), ac.signal)
          .then((data) => {
            setPeriodCountPanel(data?.exportable ?? data?.total ?? null);
            setSinXmlPanel(data?.sin_xml ?? 0);
          })
          .catch(() => {
            setPeriodCountPanel(null);
            setSinXmlPanel(0);
          });
      } else {
        setPeriodCountPanel(null);
        setSinXmlPanel(0);
      }
      if (showAmbosFlujos || hubMode) {
        fetchExportPeriodCount(paramsForDir("todos", false), ac.signal)
          .then((data) => {
            setPeriodCountTodos(data?.exportable ?? data?.total ?? null);
            setSinXmlTodos(data?.sin_xml ?? 0);
          })
          .catch(() => {
            setPeriodCountTodos(null);
            setSinXmlTodos(0);
          });
      } else {
        setPeriodCountTodos(null);
        setSinXmlTodos(0);
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [clientId, panelDir, paramsForDir, periodReady, disabled, hubMode, showAmbosFlujos]);

  const onConfigureFolder = async () => {
    if (embeddedPreview) {
      toast.info("En el visor del IDE use Descargar ZIP. Para carpetas, abra la app en Chrome o Edge.", {
        duration: 8000,
      });
      return;
    }
    setRootBusy(true);
    try {
      const { displayPath } = await pickAndSaveTaxRepoRootDirectory(clientRfc);
      refreshRootState();
      toast.success(`Carpeta base: ${displayPath}`);
    } catch (e) {
      if (isPickerUserAbort(e) || e?.name === "AbortError") return;
      toast.error(e?.message || "No se pudo guardar la carpeta");
    } finally {
      setRootBusy(false);
    }
  };

  const countForScope = useCallback(
    (dirScope) => {
      if (dirScope === "todos") return periodCountTodos;
      return periodCountPanel;
    },
    [periodCountPanel, periodCountTodos],
  );

  const sinXmlForScope = useCallback(
    (dirScope) => (dirScope === "todos" ? sinXmlTodos : sinXmlPanel),
    [sinXmlPanel, sinXmlTodos],
  );

  const buildExportAssessment = (withPdf, dirScope) =>
    assessExportBeforeGenerate({
      count: countForScope(dirScope) ?? 0,
      includePdf: withPdf,
      dirScope,
      fechaInicio,
      fechaFin,
    });

  /** Abre diálogo de confirmación (no aplica a «Volver a descargar»). */
  const requestExport = (payload) => {
    if (!clientId || !periodReady || disabled || busy) return;
    const { withPdf } = payload;
    const dirScope = exportAmbosFlujos ? "todos" : payload.dirScope;
    const assessment = buildExportAssessment(withPdf, dirScope);
    if (assessment.level === "block") {
      toast.error(assessment.message, { duration: 14000 });
      return;
    }
    setPendingExport({ ...payload, assessment });
    setConfirmOpen(true);
  };

  const periodDays = daysInPeriod(fechaInicio, fechaFin);

  const confirmTitle = useMemo(() => {
    if (!pendingExport) return "";
    const { mode, withPdf, dirScope, forceRegenerate } = pendingExport;
    const fmt = withPdf ? "XML y PDF" : "XML";
    const alcance = direccionLabel(dirScope);
    if (forceRegenerate) {
      return mode === "folder"
        ? `Volver a exportar a carpeta (${alcance})`
        : `Volver a generar ZIP (${alcance})`;
    }
    return mode === "folder"
      ? `Exportar a carpeta — ${alcance} (${fmt})`
      : `Descargar ZIP — ${alcance} (${fmt})`;
  }, [pendingExport]);

  const confirmDescription = useMemo(() => {
    if (!pendingExport) return null;
    const { withPdf, dirScope, mode, forceRegenerate, assessment } = pendingExport;
    const count = countForScope(dirScope);
    const sinXml = sinXmlForScope(dirScope);
    const rfc = (clientRfc || "").trim().toUpperCase() || "—";
    const alcance =
      dirScope === "todos"
        ? "comprobantes emitidos y recibidos"
        : `comprobantes ${direccionLabel(dirScope)} del visor`;
    const accion =
      mode === "folder"
        ? "Se copiarán a la carpeta que configuró"
        : "Se descargará un archivo ZIP con";
    const lines = [
      `${accion} los ${alcance} del cliente ${rfc}, ya cargados previamente en el sistema.`,
      `Periodo: ${formatPeriodoLegible(fechaInicio, fechaFin)}.`,
      count != null
        ? `Incluye unos ${count} comprobante(s) con archivo disponible para exportar.`
        : "Solo se incluyen comprobantes que ya tienen XML en el sistema.",
      "En este paso no se consulta de nuevo el portal del SAT.",
    ];
    if (sinXml > 0) {
      lines.push(
        `${sinXml} factura(s) que ve en la lista aún no tienen XML guardado; no entrarán en la descarga. Puede cargarlas en Carga de información.`,
      );
    }
    if (withPdf) {
      lines.push("También se generará un PDF por cada comprobante; puede tardar varios minutos.");
    }
    if (forceRegenerate) {
      lines.push("Se creará el archivo de nuevo (no reutiliza la descarga anterior).");
    }
    if (assessment?.level === "warn" && assessment.message) {
      lines.push(assessment.message);
    }
    return lines;
  }, [
    pendingExport,
    clientRfc,
    fechaInicio,
    fechaFin,
    periodDays,
    countForScope,
    sinXmlForScope,
    exportTipo,
    panelDir,
  ]);

  const runZipExport = async (withPdf, dirScope, { forceRegenerate = false, signal } = {}) => {
    const query = {
      ...paramsForDir(dirScope, withPdf),
      ...(forceRegenerate ? { force_regenerate: true } : {}),
    };
    const cacheKey = buildExportCacheKey({
      clientId,
      fechaInicio,
      fechaFin,
      direccion: dirScope,
      includePdf: withPdf,
    });

    if (!forceRegenerate) {
      const cached = await getCachedExportZip(cacheKey);
      if (cached?.blob) {
        triggerBlobDownload(cached.blob, cached.filename);
        return { filename: cached.filename, fromCache: true, postMessage: "" };
      }
    }

    const zipResult = await fetchExportZipBlob(query, signal);
    if (zipResult.fromServerCache) {
      toast.info(
        `Descarga previa disponible para el periodo ${formatPeriodoLegible(fechaInicio, fechaFin)}`,
        { duration: 6000 },
      );
    }
    const zipCheck = assessZipBlobAfterDownload(zipResult.blob, withPdf);
    if (zipCheck.level === "block") {
      triggerBlobDownload(zipResult.blob, zipResult.filename);
      return {
        filename: zipResult.filename,
        fromCache: false,
        postMessage: zipCheck.message,
        postLevel: "error",
      };
    }
    if (!zipCheck.skipCache) {
      await saveCachedExportZip(cacheKey, zipResult.blob, zipResult.filename, {
        label: `${direccionLabel(dirScope)} · ${withPdf ? "XML+PDF" : "XML"}`,
        clientId,
      });
    }
    triggerBlobDownload(zipResult.blob, zipResult.filename);
    await refreshLastCache();
    return {
      filename: zipResult.filename,
      fromCache: false,
      postMessage: zipCheck.message,
      postLevel: zipCheck.level === "warn" ? "warning" : "ok",
    };
  };

  const runFolderExport = async (withPdf, dirScope, signal) => {
    const userRoot = await getWritableTaxRepoRootHandle();
    if (!userRoot) throw new Error("Configure la carpeta base antes de exportar.");
    const manifest = await fetchExportManifest(paramsForDir(dirScope, withPdf), signal);
    if (!manifest?.items?.length) {
      throw new Error("No hay CFDI con XML en ese periodo en la base de datos");
    }
    const stats = await writeManifestToDirectory({
      userRootHandle: userRoot,
      manifest,
      clientId,
      clientRfc,
      mapItemPath: mapManifestItemToTaxRepositoryPath,
      fetchXml: fetchCfdiXmlBlob,
      signal,
    });
    return stats.writtenXml;
  };

  const onZip = async (withPdf, dirScope, forceRegenerate = false) => {
    if (!clientId || !periodReady || disabled || busy) return;
    setBusy(true);
    const abort = new AbortController();
    try {
      const { filename, fromCache, postMessage, postLevel } = await runZipExport(withPdf, dirScope, {
        forceRegenerate,
        signal: abort.signal,
      });
      if (postLevel === "error") {
        toast.error(postMessage || "ZIP demasiado grande; use un periodo más corto.", { duration: 14000 });
      } else if (postLevel === "warning" && postMessage) {
        toast.warning(postMessage, { duration: 14000 });
        toast.success(fromCache ? `ZIP reutilizado: ${filename}` : `ZIP descargado: ${filename}`);
      } else {
        toast.success(
          fromCache ? `ZIP reutilizado (sin regenerar): ${filename}` : `ZIP generado y descargado: ${filename}`,
        );
      }
    } catch (e) {
      const detail = e?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      const msg =
        code === "EXPORT_NO_XML_ON_DISK"
          ? detail?.message ||
            "Hay CFDI en el visor pero sin XML en disco. Vuelva a cargarlos en Carga de información."
          : e?.status === 404
            ? formatApiError(detail) ||
              "No hay XML exportables en MongoDB para ese periodo. Revise el contador del menú Descargar periodo."
            : e?.status === 413
              ? formatApiError(detail) ||
                "Periodo o volumen demasiado grande. Exporte por meses o solo XML."
              : formatApiError(detail) || e?.message || "No se pudo generar ZIP";
      toast.error(msg, { duration: 14000 });
    } finally {
      setBusy(false);
    }
  };

  const onRedownloadLast = async () => {
    if (!lastCache?.blob || busy) return;
    triggerBlobDownload(lastCache.blob, lastCache.filename);
    toast.success(`Descarga repetida: ${lastCache.filename}`);
  };

  const onFolder = async (withPdf, dirScope) => {
    if (!clientId || !periodReady || disabled || busy) return;
    if (embeddedPreview) {
      toast.info("Use Descargar ZIP en este visor, o abra la app en Chrome/Edge para carpetas.");
      return;
    }
    if (!taxRepoRootReady) {
      toast.error("Configure primero la carpeta base.");
      return;
    }
    setBusy(true);
    const abort = new AbortController();
    try {
      const ok = await validateStoredTaxRepoRootAccess();
      if (!ok) {
        refreshRootState();
        throw new Error("Vuelva a elegir la carpeta base en su PC.");
      }
      const written = await runFolderExport(withPdf, dirScope, abort.signal);
      toast.success(`Exportados ${written} XML a carpeta (${TAX_REPO_ROOT_FOLDER}).`);
    } catch (e) {
      if (isPickerUserAbort(e)) return;
      toast.error(formatApiError(e?.detail) || e?.message || "No se pudo exportar a carpeta");
    } finally {
      setBusy(false);
    }
  };

  const canUseFolder = supportsDirectoryPicker() && !embeddedPreview;
  const panelDisabled = disabled || !clientId || !periodReady || busy;

  const countHint = useMemo(() => {
    let base = "Periodo del visor";
    const sinXml = exportAmbosFlujos ? sinXmlTodos : Math.max(sinXmlPanel, sinXmlTodos);
    if (exportAmbosFlujos && periodCountTodos != null) {
      base = `${periodCountTodos} comprobantes exportables (emitidos y recibidos)`;
    } else if (periodCountPanel != null) base = `${periodCountPanel} CFDI con XML exportable`;
    else if (periodCountTodos != null) base = `${periodCountTodos} CFDI con XML exportable`;
    if (sinXml > 0) {
      base += ` · ${sinXml} en pantalla sin archivo XML`;
    }
    if (periodDays > EXPORT_LIMITS.DAYS_WARN) {
      return `${base} · periodo largo (${periodDays} días): mejor exportar por mes`;
    }
    return base;
  }, [exportAmbosFlujos, periodCountPanel, periodCountTodos, periodDays, sinXmlPanel, sinXmlTodos]);

  const lastCacheForPeriod =
    lastCache?.cacheKey &&
    lastCache.cacheKey.startsWith(`${clientId}|${fechaInicio}|${fechaFin}|`);

  const handleConfirmExport = async () => {
    const job = pendingExport;
    setConfirmOpen(false);
    setPendingExport(null);
    if (!job) return;
    if (job.mode === "folder") {
      await onFolder(job.withPdf, job.dirScope);
    } else {
      await onZip(job.withPdf, job.dirScope, Boolean(job.forceRegenerate));
    }
  };

  const exportConfirmDialog = (
    <AlertDialog
      open={confirmOpen}
      onOpenChange={(open) => {
        setConfirmOpen(open);
        if (!open) setPendingExport(null);
      }}
    >
      <AlertDialogContent data-testid="export-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm text-slate-600">
              {confirmDescription?.map((line) => (
                <p key={line} className="m-0 leading-relaxed">
                  {line}
                </p>
              ))}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="export-confirm-cancel">Cancelar</AlertDialogCancel>
          <AlertDialogAction
            className="bg-[#0F2D5C] hover:bg-[#0F2D5C]/90"
            data-testid="export-confirm-accept"
            onClick={(e) => {
              e.preventDefault();
              handleConfirmExport();
            }}
          >
            {pendingExport?.mode === "folder" ? "Exportar" : "Descargar"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  const menuItems = (
    <>
      <DropdownMenuLabel className="text-xs font-normal text-slate-500">{countHint}</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {exportAmbosFlujos ? (
        <>
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400 py-1">
            Emitidos y recibidos del periodo
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => requestExport({ mode: "zip", withPdf: false, dirScope: "todos" })}
            disabled={panelDisabled}
          >
            ZIP — solo XML
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => requestExport({ mode: "zip", withPdf: true, dirScope: "todos" })}
            disabled={panelDisabled}
          >
            ZIP — XML + PDF
          </DropdownMenuItem>
          {canUseFolder ? (
            <>
              <DropdownMenuItem
                onClick={() => requestExport({ mode: "folder", withPdf: false, dirScope: "todos" })}
                disabled={panelDisabled || !taxRepoRootReady}
              >
                Carpeta — solo XML
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => requestExport({ mode: "folder", withPdf: true, dirScope: "todos" })}
                disabled={panelDisabled || !taxRepoRootReady}
              >
                Carpeta — XML + PDF
              </DropdownMenuItem>
            </>
          ) : null}
        </>
      ) : (
        <>
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400 py-1">
            Este visor ({direccionLabel(panelDir)})
          </DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => requestExport({ mode: "zip", withPdf: false, dirScope: panelDir })}
            disabled={panelDisabled}
          >
            ZIP — solo XML
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => requestExport({ mode: "zip", withPdf: true, dirScope: panelDir })}
            disabled={panelDisabled}
          >
            ZIP — XML + PDF
          </DropdownMenuItem>
          {canUseFolder ? (
            <>
              <DropdownMenuItem
                onClick={() => requestExport({ mode: "folder", withPdf: false, dirScope: panelDir })}
                disabled={panelDisabled || !taxRepoRootReady}
              >
                Carpeta — solo XML
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => requestExport({ mode: "folder", withPdf: true, dirScope: panelDir })}
                disabled={panelDisabled || !taxRepoRootReady}
              >
                Carpeta — XML + PDF
              </DropdownMenuItem>
            </>
          ) : null}
        </>
      )}
      {lastCacheForPeriod ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-slate-400 py-1">
            Última generación
          </DropdownMenuLabel>
          <DropdownMenuItem onClick={onRedownloadLast} disabled={busy}>
            <Download className="w-3.5 h-3.5 mr-2" />
            Volver a descargar ({formatCacheAge(lastCache.createdAt)})
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              const parts = (lastCache.cacheKey || "").split("|");
              const dir = exportAmbosFlujos ? "todos" : parts[3] || "todos";
              const withPdf = parts[4] === "pdf";
              requestExport({ mode: "zip", withPdf, dirScope: dir, forceRegenerate: true });
            }}
            disabled={panelDisabled}
          >
            <RotateCcw className="w-3.5 h-3.5 mr-2" />
            Regenerar ZIP
          </DropdownMenuItem>
        </>
      ) : null}
      {canUseFolder ? (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onConfigureFolder} disabled={rootBusy}>
            <FolderOpen className="w-3.5 h-3.5 mr-2" />
            {taxRepoRootReady ? "Cambiar carpeta base" : "Configurar carpeta base"}
          </DropdownMenuItem>
        </>
      ) : null}
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-[10px] font-normal text-slate-400 leading-snug">
        {taxRepositoryPathHint()}
        {" · "}
        Solo documentos del periodo ya en la base de datos.
      </DropdownMenuLabel>
    </>
  );

  const ambosToggle =
    allowAmbosToggle && !hubMode && !showAmbosFlujos ? (
      <label
        className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer select-none mr-1"
        data-testid="export-incluir-ambos-toggle"
      >
        <Checkbox
          checked={incluirAmbos}
          onCheckedChange={(v) => setIncluirAmbos(Boolean(v))}
          disabled={panelDisabled}
        />
        Incluir ambos (Emitidos y Recibidos)
      </label>
    ) : null;

  if (compact) {
    return (
      <div className={`flex items-center gap-1 flex-wrap ${className}`} data-testid="export-toolbar">
        {exportConfirmDialog}
        {ambosToggle}
        {serverCache?.message ? (
          <span
            className="text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 max-w-[220px] leading-tight"
            data-testid="export-server-cache-hint"
          >
            {serverCache.message}
          </span>
        ) : null}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="rounded-sm h-8 text-xs gap-1"
              disabled={panelDisabled}
              data-testid="export-toolbar-trigger"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {exportAmbosFlujos ? "Descargar emitidos y recibidos" : "Descargar periodo"}
              <ChevronDown className="w-3 h-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-72">
            {menuItems}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`} data-testid="export-toolbar-expanded">
      {exportConfirmDialog}
      <div className="flex flex-wrap gap-2">
        {exportAmbosFlujos ? (
          <>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => requestExport({ mode: "zip", withPdf: false, dirScope: "todos" })}
              disabled={panelDisabled}
            >
              ZIP emitidos y recibidos (XML)
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => requestExport({ mode: "zip", withPdf: true, dirScope: "todos" })}
              disabled={panelDisabled}
            >
              ZIP emitidos y recibidos (XML+PDF)
            </Button>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => requestExport({ mode: "zip", withPdf: false, dirScope: panelDir })}
            disabled={panelDisabled}
          >
            ZIP ({direccionLabel(panelDir)}) XML
          </Button>
        )}
      </div>
      {countHint ? <p className="text-[10px] text-slate-500 m-0">{countHint}</p> : null}
    </div>
  );
}
